import React, { useContext, Fragment, useMemo } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { setEnableMobileMenu } from "../../reducers/ThemeOptions";
import {
  NAV_BASE_SECTION,
  NAV_PARAMETERS_SECTION,
  NAV_DESIGNERS_SECTION,
  NAV_ADMIN_SECTION,
  NAV_ANALYTICS_SECTION,
  NAV_INTEGRATIONS_SECTION,
} from "./NavItems";
import { enumsIntegrationType, enumsRolesPermissions, enumsTypeUser } from "utils/enums";
import { useTranslation } from "react-i18next";
import useLogin from "hooks/useLogin";
import useShowConsultExpedient from "customsHooks/useShowConsultExpedient";
import MetisMenu from "react-metismenu";
import Context from "contextAPI/UserContext";

const Nav = () => {
  const { t } = useTranslation();
  const {
    userType,
    rolesPermissionsGranted,
    designerForms,
    designerFormsPermissions,
    workflowAllForms,
    workflowFormsPermissions,
    integrationOptions,
  } = useContext(Context);

  const { isLogged } = useLogin();
  const { viewSubMenuExpedientConsult } = useShowConsultExpedient();

  /**
   * Builds a MetisMenu instance from a navigation configuration array.
   * @param {Array<Object>} navConfig - Navigation nodes to render.
   * @returns {JSX.Element} Rendered MetisMenu component.
   */
  const buildMenu = (navConfig) => (
    <MetisMenu
      content={translateNav(navConfig)}
      activeLinkFromLocation
      className="vertical-nav-menu"
      iconNamePrefix=""
      classNameStateIcon="pe-7s-angle-down"
    />
  );

  /**
   * Checks whether the current user has a specific role permission.
   * @param {number} permission - Permission id from enumsRolesPermissions.
   * @returns {boolean} True when the permission is granted.
   */
  const hasRolePermission = (permission) => {
    if (rolesPermissionsGranted === null) {
      return false;
    }
    return rolesPermissionsGranted.includes(permission);
  };

  const isAdmin = isLogged === true && userType === enumsTypeUser.ADMIN;
  const isStandard = isLogged === true && userType === enumsTypeUser.STANDARD;
  const hasGeneralAccess = isLogged === true && userType !== enumsTypeUser.QUERY;

  /**
   * Resolves translated labels for a navigation tree.
   * Supports labelKey values, label render functions, and nested content.
   * @param {Array<Object>} nav - Raw navigation configuration.
   * @returns {Array<Object>} Navigation configuration with resolved labels.
   */
  const translateNav = (nav) =>
    nav.map((item) => {
      let resolvedLabel = item.label;

      if (item.labelKey) {
        resolvedLabel = t(item.labelKey);
      } else if (typeof item.label === "function") {
        resolvedLabel = item.label();
      }

      const translatedItem = {
        ...item,
        label: resolvedLabel,
      };

      if (Array.isArray(item.content)) {
        translatedItem.content = translateNav(item.content);
      }

      return translatedItem;
    });

  // COMPUTE ACCESS FLAGS (used by permission functions in NavItems)
  const access = hasGeneralAccess;
  const accessTRD =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.TRD_CONSFIGURATION));
  const accessDocumentaryType =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.DOCUMENTAL_TYPES));
  const accessConfigurationApp =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.APPLICATION_SETUP));
  const accessDesignerForms = isAdmin || (isStandard && designerForms === true);
  const accessDesignerFormsConsult =
    isStandard && Array.isArray(designerFormsPermissions) && designerFormsPermissions.length !== 0;
  const accessWorkflowsForms = isAdmin || (isStandard && workflowAllForms === true);
  const accessWorkflowsFormsConsult =
    isStandard && Array.isArray(workflowFormsPermissions) && workflowFormsPermissions.length !== 0;
  const accessAdminThirdPartyAccounts =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.THIRD_PARTY_ACCOUNT));
  const accessList =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.LIST_DESIGNER));
  const accessUserManager =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.USER_MANAGER));
  const accessRoles =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.ROLE_MANAGER));
  const accessIntegrations = isAdmin;
  const accessWorkGroups =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.WORK_GROUP_MANAGER));
  const accessProcessManagement =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.PROCESS_MANAGEMENT));
  const accessMetrics = isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.METRICS));
  const accessAuditLogs =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.AUDIT_LOG));
  const accessTemplateDesigner =
    isAdmin || (isStandard && hasRolePermission(enumsRolesPermissions.TEMPLATE_DESIGNER));

  // PERMISSION CONTEXT - passed to permission functions in nav items
  const permissionContext = {
    isAdmin,
    access,
    accessTRD,
    accessDocumentaryType,
    accessConfigurationApp,
    accessDesignerForms,
    accessDesignerFormsConsult,
    accessWorkflowsForms,
    accessWorkflowsFormsConsult,
    accessAdminThirdPartyAccounts,
    accessList,
    accessUserManager,
    accessRoles,
    accessIntegrations,
    accessWorkGroups,
    accessProcessManagement,
    accessMetrics,
    accessAuditLogs,
    accessTemplateDesigner,
  };

  /**
   * Filters navigation items recursively based on permission functions.
   * Items with a permission function are only shown if permission(context) returns true.
   * Items without a permission function are always shown.
   * If an item has content and no items pass the filter, the parent is hidden.
   * @param {Array<Object>} navArray - Navigation tree to evaluate.
   * @returns {Array<Object>} Filtered navigation tree with only allowed items.
   */
  const filterNavByPermission = (navArray) => {
    if (!Array.isArray(navArray)) return navArray;

    return navArray
      .map((item) => {
        let content = undefined;
        if (item.content) {
          content = filterNavByPermission(item.content);
        }
        return {
          ...item,
          content,
        };
      })
      .filter((item) => {
        // If item has a permission function, evaluate it
        if (item.permission && typeof item.permission === "function") {
          return item.permission(permissionContext);
        }
        // If item has content, only show if some items remain after filtering
        if (item.content && Array.isArray(item.content)) {
          return item.content.length > 0;
        }
        // Otherwise show the item
        return true;
      });
  };

  const DashboardNav = NAV_BASE_SECTION.dashboard;
  const CreateRecordNav = NAV_BASE_SECTION.createRecord;
  const ConsultRecordNav = NAV_BASE_SECTION.consultRecord;
  const ConsultRecordNavTrdExplorer = NAV_BASE_SECTION.consultRecordTrdExplorer;
  const TaskBoxNav = NAV_BASE_SECTION.taskBox;
  const ReportsNav = NAV_BASE_SECTION.reports;
  const ProcessManagementNav = NAV_BASE_SECTION.processManagement;
  const TrashNav = NAV_BASE_SECTION.trash;
  const DataExportNav = NAV_BASE_SECTION.dataExport;

  const ParameterNav = NAV_PARAMETERS_SECTION.parameter;
  const DesignerNav = NAV_DESIGNERS_SECTION.designer;

  const AdminNav = NAV_ADMIN_SECTION.admin;
  const MetricsNav = NAV_ANALYTICS_SECTION.metrics;
  const IntegrationsNav = NAV_INTEGRATIONS_SECTION.integrations;

  let consultRecordNav = ConsultRecordNav;
  if (viewSubMenuExpedientConsult === true) {
    consultRecordNav = ConsultRecordNavTrdExplorer;
  }

  let reportsNav = null;
  if (
    integrationOptions !== null &&
    (integrationOptions.includes(enumsIntegrationType.ELECTRONIC_SIGNATURE) ||
      integrationOptions.includes(enumsIntegrationType.VIRTUAL_TOKEN) ||
      integrationOptions.includes(enumsIntegrationType.BIOFIRMA))
  ) {
    reportsNav = ReportsNav;
  }

  let processManagementNav = null;
  if (access === true && accessProcessManagement === true) {
    processManagementNav = ProcessManagementNav;
  }

  let filteredDataExportNav = null;
  if (access === true) {
    filteredDataExportNav = filterNavByPermission(DataExportNav);
  }
  let finalDataExportNav = null;
  if (filteredDataExportNav && filteredDataExportNav.length > 0) {
    finalDataExportNav = filteredDataExportNav;
  }

  let filteredParameterNav = null;
  if (access === true) {
    filteredParameterNav = filterNavByPermission(ParameterNav);
  }
  let finalParameterNav = null;
  if (filteredParameterNav && filteredParameterNav.length > 0) {
    finalParameterNav = filteredParameterNav;
  }

  let filteredDesignerNav = null;
  if (access === true) {
    filteredDesignerNav = filterNavByPermission(DesignerNav);
  }
  let finalDesignerNav = null;
  if (filteredDesignerNav && filteredDesignerNav.length > 0) {
    finalDesignerNav = filteredDesignerNav;
  }

  let filteredAdminNav = null;
  if (access === true) {
    filteredAdminNav = filterNavByPermission(AdminNav);
  }
  let filteredMetricsNav = null;
  if (access === true) {
    filteredMetricsNav = filterNavByPermission(MetricsNav);
  }

  let integrationsNav = null;
  if (access === true && accessIntegrations === true) {
    integrationsNav = IntegrationsNav;
  }

  let integrationsTitle = null;
  if (access === true && accessIntegrations === true) {
    integrationsTitle = <h5 className="app-sidebar__heading">{t("integrations.nav")}</h5>;
  }

  const primaryNavSequence = useMemo(
    () => [
      DashboardNav,
      CreateRecordNav,
      consultRecordNav,
      TaskBoxNav,
      reportsNav,
      processManagementNav,
      TrashNav,
      finalDataExportNav,
    ],
    [
      DashboardNav,
      CreateRecordNav,
      consultRecordNav,
      TaskBoxNav,
      reportsNav,
      processManagementNav,
      TrashNav,
      finalDataExportNav,
    ],
  );

  let adminNavToShow = null;
  if (filteredAdminNav && filteredAdminNav.length > 0) {
    adminNavToShow = filteredAdminNav;
  }
  let metricsNavToShow = null;
  if (filteredMetricsNav && filteredMetricsNav.length > 0) {
    metricsNavToShow = filteredMetricsNav;
  }

  let sectionTitle = null;
  if (
    finalParameterNav !== null ||
    finalDesignerNav !== null ||
    adminNavToShow !== null ||
    metricsNavToShow !== null
  ) {
    sectionTitle = <h5 className="app-sidebar__heading">{t("verticalNav.tittle2")}</h5>;
  }

  const managedNavSequence = useMemo(
    () => [finalParameterNav, finalDesignerNav, adminNavToShow, metricsNavToShow],
    [finalParameterNav, finalDesignerNav, adminNavToShow, metricsNavToShow],
  );

  return (
    <Fragment>
      <h5 className="app-sidebar__heading"> </h5>
      {primaryNavSequence.map((navConfig, index) => {
        if (navConfig === null) {
          return null;
        }

        if (index === 1) {
          return (
            <Fragment key={"primary-" + index}>
              <h5 className="app-sidebar__heading">{t("verticalNav.tittle1")}</h5>
              {buildMenu(navConfig)}
            </Fragment>
          );
        }

        return <Fragment key={"primary-" + index}>{buildMenu(navConfig)}</Fragment>;
      })}

      {sectionTitle}

      {managedNavSequence.map((navConfig, index) => {
        if (navConfig === null) {
          return null;
        }

        return <Fragment key={"managed-" + index}>{buildMenu(navConfig)}</Fragment>;
      })}

      {integrationsTitle}

      {integrationsNav && buildMenu(integrationsNav)}
    </Fragment>
  );
};

/**
 *Maps the state properties to the props of a component for accessing 'enableMobileMenu' state from the Redux store.
 *@param {Object} state - The current state object from the Redux store.
 *@returns {Object} An object containing the 'enableMobileMenu' property that will be accessible as a prop in the component.
 */
const mapStateToProps = (state) => ({
  enableMobileMenu: state.ThemeOptions.enableMobileMenu,
});

/**
 *Connects the 'setEnableMobileMenu' action creator to the component's props,
 *allowing the component to dispatch the action to enable or disable the mobile menu through the Redux store.
 *@param {function} dispatch - A function provided by Redux to dispatch actions to the store
 *@returns {object} An object containing the 'setEnableMobileMenu' prop, which is a function that can be used to
 *dispatch the action to enable or disable the mobile menu.
 */
const mapDispatchToProps = (dispatch) => ({
  setEnableMobileMenu: (enable) => dispatch(setEnableMobileMenu(enable)),
});
export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Nav));
